import { languageSchema } from "components/shared/event/schema";
import { FieldObjectType, InputType, SupportedLanguage, ValidationType } from "interfaces";
import { get, isEmpty } from "lodash";
import { REGEX_VALID_TEMPLATE_KEY } from "utills/constants";
import i18n from "utills/i18n";
import { z } from "zod";

export const FieldSchema = (languageSchema: any, languages: SupportedLanguage[]) => {
  return z.object({
    object: z.string().optional(),
    validation: z.string().optional(),
    label: languageSchema,
    sub_label: z.object({
      en: z.string().optional().nullable(),
      vi: z.string().optional().nullable(),
    }),
    sub_label_url: z.string().optional(),
    type: z.string().min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
    key: z
      .string()
      .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired"))
      .refine((value) => REGEX_VALID_TEMPLATE_KEY.test(value ?? ""), i18n.t("validation.errorMessage.invalidFieldKey")),
    require: z.boolean(),
    show: z.boolean(),
    is_lock_update_data: z.boolean(),
    option: z.any(),
  }).superRefine(({ validation, option, object, type }, ctx) => {
    if (validation === ValidationType.LENGTH || validation === ValidationType.DOB) {
      if (isNaN(get(option, "min", 0))) {
        ctx.addIssue({
          path: ["option.min"],
          code: "custom",
          message: i18n.t("validation.errorMessage.thisFieldIsRequired"),
        });
      }
      if (isNaN(get(option, "max", 0))) {
        ctx.addIssue({
          path: ["option.max"],
          code: "custom",
          message: i18n.t("validation.errorMessage.thisFieldIsRequired"),
        });
      }
      if (get(option, "max", 0) < get(option, "min", 0)) {
        ctx.addIssue({
          path: ["option.max"],
          code: "custom",
          message: i18n.t("validation.errorMessage.minNum", { min: i18n.t("eventSetup.participantTemplate.field.fieldName.min") }),
        });
      }
    }
    if (Array.isArray(option)) {
      option.forEach((opt, index) => {
        if (isEmpty(get(opt, "key"))) {
          ctx.addIssue({
            path: [`option.${index}.key`],
            code: "custom",
            message: i18n.t("validation.errorMessage.thisFieldIsRequired"),
          });
        }
        languages.forEach((key) => {
          if (isEmpty(get(opt, `label.${key}`, null))) {
            ctx.addIssue({
              path: [`option.${index}.label.${key}`],
              code: "custom",
              message: i18n.t("validation.errorMessage.thisFieldIsRequired"),
            });  
          }
        })
      })
    }
    if (type === InputType.SELECT && isEmpty(object)) {
      ctx.addIssue({
        path: [`object`],
        code: "custom",
        message: i18n.t("validation.errorMessage.thisFieldIsRequired"),
      });
    }
  })
}

export const AttendantTemplateSchema = (languages: SupportedLanguage[]) => {
  return z.object({
    event_id: z.string().optional(),
    languages: z.array(z.string()).optional(),
    templates: z
      .array(
        z.object({
          attendant_template_name: z.string().min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
          data: z.array(
            z.object({
              fields: z.array(
                FieldSchema(languageSchema(languages), languages)
              ),
              group: z.object({
                group_key: z.string().min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
                label: languageSchema(languages),
              }),
            })
          ),
        })
      )
      .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
  });
};
